<template>
    <HeaderContent title="Package Detail" />
    <BCard body-class="p-4 text-left" style="font-family: 'Segoe UI', Arial, sans-serif;">
        <!-- Header Grid -->
        <div class="row mb-4">
            <div class="col-md-4 mb-2">
                <div class="font-weight-bold small text-dark">Package Name</div>
                <div class="mt-1">{{ packageItems?.name ?? "-" }}</div>
            </div>
            <div class="col-md-4 mb-2">
                <div class="font-weight-bold small text-dark">Pax</div>
                <div class="mt-1">{{ packageItems?.pax ?? "-" }} Pax</div>
            </div>
            <div class="col-md-4 mb-2">
                <div class="font-weight-bold small text-dark">Duration</div>
                <div class="mt-1">{{ packageItems?.duration_days ?? "-" }} Days</div>
            </div>
        </div>

        <hr class="mb-4">

        <!-- Program Table -->
        <div class="table-responsive">
            <table class="table table-bordered table-sm">
                <thead class="bg-light">
                    <tr>
                        <th class="py-2" style="width: 50px;">Day</th>
                        <th class="py-2">Name</th>
                        <th class="py-2" colspan="2">Destination</th>
                        <th class="py-2">Accommodation</th>
                        <th class="py-2">Transportation</th>
                        <th class="py-2">Activity</th>
                        <th class="py-2">Meal</th>
                        <th class="py-2 text-end" style="width: 150px;">Sub Total</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(item, index) in packageItems?.programs"
                        :key="index"
                    >
                        <td class="text-center fw-bold">{{ item.day }}</td>
                        <td class="small">{{ item.name }}</td>
                        <td class="small">{{ item.from_destination?.name || '-' }}</td>
                        <td class="small">{{ item.to_destination?.name || '-' }}</td>
                        <td>
                            <ul class="list-unstyled mb-0 small" v-if="item.accommodations?.length">
                                    <li v-for="(acc, i) in item.accommodations" :key="i" class="mb-1">
                                        • {{ acc.name }}
                                        <div class="x-small text-muted ps-2">
                                            {{ acc.room_type }} {{ acc.extra_bed == 1 ? '+ Extra Bed' : '' }}
                                            ({{ formatRupiah(acc.total_price || (acc.price * acc.pax)) }})
                                        </div>
                                    </li>
                            </ul>
                            <span v-else class="text-muted small">-</span>
                        </td>
                        <td>
                                <ul class="list-unstyled mb-0 small" v-if="item.transportations?.length">
                                    <li v-for="(trp, i) in item.transportations" :key="i">
                                        • {{ trp.name }} ({{ formatRupiah(trp.sub_total || (trp.price * trp.pax)) }})
                                    </li>
                                </ul>
                            <span v-else class="text-muted small">-</span>
                        </td>
                        <td>
                            <div class="font-weight-bold small">{{ item.name }}</div>
                            <ul class="list-unstyled mb-0 small" v-if="item.activities?.length">
                                    <li v-for="(act, i) in item.activities" :key="i">
                                        • {{ act.name }} ({{ formatRupiah(act.sub_total || (act.price * act.pax)) }})
                                        <!-- Description -->
                                        <div class="x-small text-muted ps-2">
                                            {{ act.description }}
                                        </div>
                                    </li>
                                </ul>
                        </td>
                        <td>
                                <ul class="list-unstyled mb-0 small" v-if="item.meals?.length">
                                    <li v-for="(meal, i) in item.meals" :key="i">
                                        • {{ meal.name }} ({{ formatRupiah(meal.sub_total || (meal.price * (meal.pax || packageItems.pax))) }})
                                    </li>
                                </ul>
                            <span v-else class="text-muted small">-</span>
                        </td>
                        <td class="text-end fw-bold">
                            {{ formatRupiah(item.sub_total || item.price) }}
                        </td>
                    </tr>
                    
                    <tr v-if="packageItems?.programs?.length" class="font-weight-bold">
                         <td colspan="7" class="text-end pb-3">
                                <span class="text-right text-muted italic small">Grand Total:</span>
                            </td>
                        <td colspan="2" class="text-right mb-0" style="background-color: #e1eed9;">
                            {{ formatRupiah(packageItems?.sub_total || packageItems?.price) }}
                        </td>
                    </tr>
                    
                    <tr v-if="packageItems?.programs?.length">
                         <td colspan="7" class="text-end pb-3">
                                <span class="text-right text-muted italic small">Price per Person ({{ packageItems?.pax }} pax):</span>
                            </td>
                        <td colspan="2" class="text-right mb-0">
                            {{ formatRupiah(packageItems?.price / packageItems?.pax) }}
                        </td>
                    </tr>

                    <tr v-if="!packageItems?.programs?.length">
                        <td class="text-center py-3 text-muted" colspan="9">
                            No program data available
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Inclusions / Exclusions -->
        <div class="row mt-4">
            <div class="col-md-6">
                <h6 class="fw-bold mb-2 border-bottom pb-2">Inclusions</h6>
                <ul v-if="packageItems?.inclusion" class="list-unstyled small ps-2">
                    <li v-for="(item, index) in packageItems.inclusion.split('\n')" :key="index" class="mb-1">
                        • {{ item }}
                    </li>
                </ul>
                <p v-else class="text-muted small">-</p>
            </div>
            <div class="col-md-6">
                <h6 class="fw-bold border-bottom pb-2 mb-2">Exclusions</h6>
                <ul v-if="packageItems?.exclusion" class="list-unstyled small ps-2">
                    <li v-for="(item, index) in packageItems.exclusion.split('\n')" :key="index" class="mb-1">
                        • {{ item }}
                    </li>
                </ul>
                <p v-else class="text-muted small">-</p>
            </div>
        </div>

        <template #footer>
            <div class="d-flex justify-content-end">
                <BButton
                    type="button"
                    @click="() => router.push('/packages/edit/' + route.params.uuid)"
                    variant="success"
                    class="me-2"
                >
                    <i class="fas fa-edit me-1"></i> Edit
                </BButton>
                <BButton
                    type="button"
                    @click="() => router.back()"
                    variant="secondary"
                >
                    <i class="fas fa-arrow-left me-1"></i> Back
                </BButton>
            </div>
        </template>
    </BCard>
</template>

<script setup>
import axios from "axios";
import HeaderContent from "@/components/HeaderContent.vue";
import { onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useLoading } from "vue-loading-overlay";
import { useAuthStore } from "@/store/auth.js";
import { formatRupiah } from "@/utils/format";
import { BButton, BCard } from "bootstrap-vue-next";

const auth = useAuthStore();
const $loading = useLoading();
const route = useRoute();
const router = useRouter();

const packageItems = ref();
const getPackage = async () => {
    const loader = $loading.show({
        color: "#489464",
        loader: "spinner",
    });
    try {
        const response = await axios.get(
            `${process.env.VUE_APP_API_URL}/package/detail/${route.params.uuid}`,
            {
                headers: {
                    "Content-type": "application/json",
                    Authorization: "Bearer " + auth._token,
                },
            }
        );

        const res = response.data;

        if (res.status) {
            packageItems.value = res.data;
        }
    } finally {
        loader.hide();
    }
};

onMounted(async () => {
    await getPackage();
});
</script>
